// «Cómo lo usan» (admin F3, 24-oct-2026): chips + panel de detalle por negocio, para las listas
// Negocios y Suscripciones y la tarjeta de la ficha.
//
// Decisiones (plan §7):
//  - No es un tooltip puro: 178 de 242 vistas del admin son desde el celular y ahí no hay hover.
//    En escritorio se abre al pasar el ratón o al tocar; en móvil, al tocar, como hoja inferior.
//  - La «probabilidad de renovar» NO es un porcentaje: es un riesgo alto/medio/bajo con sus motivos.
//  - Lo que se pinta por giro: lealtad (visitas, canjes, funciones PRO que usa), agenda dental/spa
//    (citas propias, confirmadas, cobros), POS (ventas, cajas vivas). El gratis enseña qué función
//    bloqueada intentó abrir (qué le interesa), no riesgo.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';
import { Activity, AlertTriangle, CheckCircle2, Clock, Lock, Sparkles, Store, Users, X } from 'lucide-react';
import { adminUsoApi, type UsoNegocio as Uso, type RiesgoRenovacion } from '../../../api/adminUsoApi';

// ───────── carga por lote con caché corta (las dos listas paginan; la ficha pide uno) ─────────
const CACHE_MS = 5 * 60 * 1000;
const cache = new Map<string, { at: number; uso: Uso }>();

export function useUsoNegocios(ids: Array<string | number>) {
  const [mapa, setMapa] = useState<Record<string, Uso>>({});
  const [cargando, setCargando] = useState(false);
  const clave = useMemo(() => [...new Set(ids.map(String).filter(Boolean))].sort().join(','), [ids]);
  useEffect(() => {
    if (!clave) { setMapa({}); return; }
    const todos = clave.split(',');
    const ahora = Date.now();
    const listos: Record<string, Uso> = {};
    const faltan: string[] = [];
    for (const id of todos) {
      const c = cache.get(id);
      if (c && ahora - c.at < CACHE_MS) listos[id] = c.uso; else faltan.push(id);
    }
    setMapa(listos);
    if (!faltan.length) return;
    let vivo = true;
    setCargando(true);
    adminUsoApi.lote(faltan).then((r) => {
      if (!vivo) return;
      const nuevos: Record<string, Uso> = {};
      for (const [id, uso] of Object.entries(r.tenants || {})) { cache.set(id, { at: Date.now(), uso }); nuevos[id] = uso; }
      setMapa((prev) => ({ ...prev, ...nuevos }));
    }).catch(() => { /* la lista sigue sirviendo sin la columna */ }).finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [clave]);
  return { uso: mapa, cargando };
}

// ───────── etiquetas ─────────
const MOTIVO: Record<string, string> = {
  sin_acceso_30d: 'Nadie ha entrado en 30 días',
  sin_visitas_30d: 'Sin visitas ni puntos en 30 días',
  sin_citas_30d: 'Sin citas propias en 30 días',
  nada_automatico: 'Nada automático funcionando',
  vence_pronto_sin_autopay: 'Vence en 30 días o menos sin cobro automático',
};
const FEATURE: Record<string, string> = {
  'gift-cards': 'Gift cards', 'recharge-points': 'Recargar puntos', 'welcome-message': 'Mensaje de bienvenida',
  whatsapp: 'WhatsApp del negocio', campaigns: 'Campañas', 'inactive-clients': 'Clientes inactivos',
  'birthday-panel': 'Cumpleaños', referrals: 'Referidos', 'unsatisfied-clients': 'Clientes insatisfechos',
  'visit-message': 'Mensaje de visita', 'referral-message': 'Mensaje de referido', 'low-balance-alert': 'Aviso de saldo bajo',
  tags: 'Etiquetas', memberships: 'Membresías', team: 'Equipo', 'employee-report': 'Reporte por empleado',
  'google-reviews': 'Reseñas en Google', 'membership-notices': 'Avisos de membresía',
};
const GIRO: Record<string, string> = { lealtad: 'Lealtad', dental: 'Consultorio', spa: 'Spa', letsmove: 'Academia', clublectura: 'Club', bots: 'Bots' };

export const haceDias = (isoStr: string | null | undefined): number | null => {
  if (!isoStr) return null;
  const t = new Date(isoStr).getTime();
  return Number.isFinite(t) ? Math.floor((Date.now() - t) / 86400000) : null;
};
export const textoHace = (isoStr: string | null | undefined): string => {
  const d = haceDias(isoStr);
  if (d == null) return 'Nunca';
  if (d === 0) return 'Hoy';
  if (d === 1) return 'Ayer';
  return `Hace ${d} d`;
};
const dinero = (n: number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);
const pct = (parte: number, total: number) => (total > 0 ? `${Math.round((parte / total) * 100)} %` : '—');

const RIESGO_CLASES: Record<RiesgoRenovacion, string> = {
  alto: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300',
  medio: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
  bajo: 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300',
  nuevo: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
};
const RIESGO_TEXTO: Record<RiesgoRenovacion, string> = { alto: 'Riesgo alto', medio: 'Riesgo medio', bajo: 'Va bien', nuevo: 'Nuevo' };

// Frase corta de actividad según el giro (para el chip del medio).
export function fraseActividad(u: Uso): { texto: string; alerta: boolean } {
  if (u.agenda) {
    const n = u.agenda.citas_propias_30d;
    return { texto: `${n} cita${n === 1 ? '' : 's'} · 30 d`, alerta: n === 0 };
  }
  if (u.pos && u.pos.available && u.lealtad && u.lealtad.visitas_30d === 0) {
    return { texto: `${u.pos.ventas_30d ?? 0} ventas · 30 d`, alerta: !(u.pos.ventas_30d ?? 0) };
  }
  if (u.lealtad) {
    const n = u.lealtad.visitas_30d + u.lealtad.ajustes_mano_30d;
    return { texto: `${u.lealtad.visitas_30d} visitas · 30 d`, alerta: n === 0 };
  }
  return { texto: `${u.personas.dias_30d} días activo`, alerta: u.personas.dias_30d === 0 };
}

const Chip: React.FC<{ className?: string; children: React.ReactNode; testid?: string; title?: string }> = ({ className = '', children, testid, title }) => (
  <span data-testid={testid} title={title} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${className}`}>{children}</span>
);

// ───────── chips (lo que se ve en la fila) ─────────
export const UsoChips: React.FC<{ uso: Uso | undefined; cargando?: boolean }> = ({ uso, cargando }) => {
  if (!uso) return <span className="text-xs text-content-3" data-testid="uso-chips-vacio">{cargando ? 'Midiendo…' : '—'}</span>;
  const dAcceso = haceDias(uso.acceso.ultimo);
  const act = fraseActividad(uso);
  const neutro = 'bg-canvas text-content-2';
  const ambar = 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300';
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="uso-chips">
      <Chip testid="uso-chip-acceso" title="Último rastro de alguien del negocio (sin contar nuestras entradas)" className={dAcceso == null || dAcceso > 30 ? ambar : neutro}>
        <Clock className="h-3 w-3" />{textoHace(uso.acceso.ultimo)}
      </Chip>
      <Chip testid="uso-chip-actividad" className={act.alerta ? ambar : neutro}><Activity className="h-3 w-3" />{act.texto}</Chip>
      {uso.renovacion ? (
        <Chip testid="uso-chip-riesgo" className={RIESGO_CLASES[uso.renovacion.riesgo]} title={uso.renovacion.motivos.map((m) => MOTIVO[m] || m).join(' · ') || 'Entra, usa y tiene algo automático'}>
          {uso.renovacion.riesgo === 'alto' ? <AlertTriangle className="h-3 w-3" /> : uso.renovacion.riesgo === 'bajo' ? <CheckCircle2 className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
          {RIESGO_TEXTO[uso.renovacion.riesgo]}
        </Chip>
      ) : uso.gratis && uso.gratis.bloqueos_60d.length > 0 ? (
        <Chip testid="uso-chip-interes" className="bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-300" title="Función PRO bloqueada que más intentó abrir en 60 días">
          <Lock className="h-3 w-3" />{FEATURE[uso.gratis.bloqueos_60d[0].feature] || uso.gratis.bloqueos_60d[0].feature}
        </Chip>
      ) : uso.demo ? <Chip className={neutro}>Demo</Chip> : null}
    </div>
  );
};

// ───────── detalle (popover y tarjeta de la ficha) ─────────
const Fila: React.FC<{ k: string; v: React.ReactNode; alerta?: boolean }> = ({ k, v, alerta }) => (
  <div className="flex items-baseline justify-between gap-3 text-sm">
    <dt className="text-content-3">{k}</dt>
    <dd className={`font-medium text-right ${alerta ? 'text-amber-600 dark:text-amber-400' : 'text-content'}`}>{v}</dd>
  </div>
);
const Bloque: React.FC<{ titulo: string; icono: React.ElementType; children: React.ReactNode; testid?: string }> = ({ titulo, icono: Icono, children, testid }) => (
  <section data-testid={testid}>
    <h4 className="text-[11px] font-bold uppercase tracking-wider text-content-3 flex items-center mb-1.5"><Icono className="h-3.5 w-3.5 mr-1.5 text-accent" />{titulo}</h4>
    <dl className="space-y-1">{children}</dl>
  </section>
);

export const UsoDetalle: React.FC<{ uso: Uso; tenantId: string | number; conEnlaceFicha?: boolean }> = ({ uso, tenantId, conEnlaceFicha }) => {
  const l = uso.lealtad, a = uso.agenda, p = uso.pos;
  const usadas: string[] = [];
  if (l) {
    if (l.cumple_60d > 0) usadas.push(`Cumpleaños (${l.cumple_60d} en 60 d)`);
    if (l.campanas_60d > 0) usadas.push(`Campañas (${l.campanas_60d} envíos)`);
    if (l.membresias > 0) usadas.push(`Membresías (${l.membresias})`);
    if (l.gift_cards > 0) usadas.push(`Gift cards (${l.gift_cards})`);
    if (l.etiquetas > 0) usadas.push(`Etiquetas (${l.etiquetas})`);
    if (l.sucursales > 1) usadas.push(`${l.sucursales} sucursales`);
    if (l.tareas_60d > 0) usadas.push(`Tareas (${l.tareas_60d})`);
    if (l.whatsapp_propio?.estado === 'WORKING') usadas.push('WhatsApp propio');
    if (l.analiticas_60d > 0) usadas.push(`Analíticas (${l.analiticas_60d} veces)`);
    if (l.exportaciones_60d > 0) usadas.push(`Exportar (${l.exportaciones_60d})`);
  }
  return (
    <div className="space-y-4" data-testid="uso-detalle">
      <Bloque titulo="Acceso y personas" icono={Users} testid="uso-bloque-acceso">
        <Fila k="Último rastro" v={textoHace(uso.acceso.ultimo)} alerta={(haceDias(uso.acceso.ultimo) ?? 99) > 30} />
        <Fila k="Usuarios dados de alta" v={uso.personas.altas} />
        <Fila k="Regresan (30 d)" v={`${uso.personas.activas_30d} persona${uso.personas.activas_30d === 1 ? '' : 's'} · ${uso.personas.dias_30d} día${uso.personas.dias_30d === 1 ? '' : 's'}`} alerta={uso.personas.activas_30d === 0} />
      </Bloque>

      {l && (
        <Bloque titulo={`${GIRO[uso.giro] || 'Lealtad'} · 30 días`} icono={Activity} testid="uso-bloque-lealtad">
          <Fila k="Clientes" v={`${l.clientes.toLocaleString('es-MX')} (+${l.clientes_nuevos_30d})`} />
          <Fila k="Visitas" v={l.visitas_30d} alerta={l.visitas_30d === 0 && l.ajustes_mano_30d === 0} />
          <Fila k="Canjes" v={l.canjes_30d} />
          {l.ajustes_mano_30d > 0 && <Fila k="Puntos a mano" v={l.ajustes_mano_30d} />}
          <div className="text-sm pt-1">
            <dt className="text-content-3 mb-1">{uso.plan.pago ? 'Funciones PRO que usa' : 'Funciones que usa'}</dt>
            <dd className="flex flex-wrap gap-1" data-testid="uso-funciones">
              {usadas.length ? usadas.map((f) => <Chip key={f} className="bg-canvas text-content-2">{f}</Chip>)
                : <span className={`font-medium ${uso.plan.pago ? 'text-amber-600 dark:text-amber-400' : 'text-content'}`}>Solo registrar visitas</span>}
            </dd>
          </div>
        </Bloque>
      )}

      {a && (
        <Bloque titulo={`${GIRO[uso.giro] || 'Agenda'} · 30 días`} icono={Activity} testid="uso-bloque-agenda">
          <Fila k="Citas agendadas por ellos" v={`${a.citas_propias_30d} · ${a.agendan} persona${a.agendan === 1 ? '' : 's'} agendan`} alerta={a.citas_propias_30d === 0} />
          {a.importadas_30d > 0 && <Fila k="Importadas (no cuentan)" v={a.importadas_30d} />}
          <Fila k="Confirmadas" v={`${pct(a.confirmadas_30d, a.pasadas_30d)} de ${a.pasadas_30d}`} alerta={a.pasadas_30d > 10 && a.confirmadas_30d / a.pasadas_30d < 0.3} />
          <Fila k="Asistencia registrada" v={pct(a.asistencia_30d, a.pasadas_30d)} />
          <Fila k="Cobros" v={a.cobros_30d ? `${a.cobros_30d} · ${dinero(a.monto_30d)}` : 'No cobran aquí'} alerta={a.cobros_30d === 0 && a.citas_propias_30d > 0} />
          <Fila k="Cotizaciones" v={a.cotizaciones_30d} />
          <Fila k="Avisos WhatsApp" v={a.wa_30d ? `${a.wa_ok_30d}/${a.wa_30d} entregados` : 'Ninguno'} alerta={a.citas_propias_30d > 0 && a.wa_30d === 0} />
          <Fila k="Doctores" v={a.doctores} />
        </Bloque>
      )}

      {uso.tiene_pos && (
        <Bloque titulo="Punto de venta · 30 días" icono={Store} testid="uso-bloque-pos">
          {p && p.available ? (
            <>
              <Fila k="Ventas" v={`${p.ventas_30d} · ${dinero(p.monto_30d || 0)}`} alerta={!p.ventas_30d} />
              <Fila k="Días con venta" v={p.dias_venta_30d} />
              <Fila k="Último ticket" v={textoHace(p.ultimo_ticket)} />
              <Fila k="Cajas vivas (48 h)" v={`${p.dispositivos_vivos}/${p.dispositivos}`} alerta={!p.dispositivos_vivos} />
              <Fila k="Turnos cerrados" v={p.turnos_30d} />
            </>
          ) : <p className="text-xs text-content-3">{p?.error ? `POS sin datos (${p.error}).` : 'POS sin datos.'}</p>}
        </Bloque>
      )}

      {uso.renovacion && (
        <Bloque titulo="Renovación" icono={uso.renovacion.riesgo === 'alto' ? AlertTriangle : CheckCircle2} testid="uso-bloque-renovacion">
          <div className="flex items-center gap-2 mb-1">
            <Chip className={RIESGO_CLASES[uso.renovacion.riesgo]}>{RIESGO_TEXTO[uso.renovacion.riesgo]}</Chip>
            {uso.plan.days_left != null && <span className="text-xs text-content-3">vence en {uso.plan.days_left} d{uso.plan.autopay ? ' · cobro automático' : ''}</span>}
          </div>
          {uso.renovacion.riesgo === 'nuevo' ? (
            <p className="text-xs text-content-3">Alta hace {uso.renovacion.dias_alta} días: todavía no hay 30 días que juzgar.</p>
          ) : uso.renovacion.motivos.length ? (
            <ul className="text-sm space-y-0.5">{uso.renovacion.motivos.map((m) => <li key={m} className="flex items-start"><AlertTriangle className="h-3.5 w-3.5 mr-1.5 mt-0.5 text-amber-500 flex-shrink-0" />{MOTIVO[m] || m}</li>)}</ul>
          ) : <p className="text-sm text-content-2">Entra, lo usa y tiene algo automático funcionando.</p>}
        </Bloque>
      )}

      {uso.gratis && (
        <Bloque titulo="Qué le interesa (gratis)" icono={Lock} testid="uso-bloque-gratis">
          {uso.gratis.bloqueos_60d.length ? (
            <div className="flex flex-wrap gap-1">{uso.gratis.bloqueos_60d.slice(0, 6).map((b) => <Chip key={b.feature} className="bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-300">{FEATURE[b.feature] || b.feature} · {b.n}</Chip>)}</div>
          ) : <p className="text-xs text-content-3">No ha intentado abrir ninguna función PRO en 60 días.</p>}
          {uso.gratis.analiticas_60d > 0 && <p className="text-xs text-content-3 mt-1">Abrió analíticas {uso.gratis.analiticas_60d} veces.</p>}
        </Bloque>
      )}

      {conEnlaceFicha && (
        <RouterLink to={`/dashboard/admin/negocios/${tenantId}`} className="inline-block text-xs text-accent hover:underline" data-testid="uso-ir-ficha">Abrir la ficha del negocio →</RouterLink>
      )}
    </div>
  );
};

// ───────── celda: chips + panel (hover/toque en escritorio, hoja inferior en móvil) ─────────
export const UsoCelda: React.FC<{ tenantId: string | number; uso: Uso | undefined; cargando?: boolean; nombre?: string | null }> = ({ tenantId, uso, cargando, nombre }) => {
  const [abierto, setAbierto] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const ancla = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);

  const esMovil = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;

  const abrir = () => {
    if (!uso) return;
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    if (!esMovil() && ancla.current) {
      const r = ancla.current.getBoundingClientRect();
      const ancho = 340;
      const left = Math.min(Math.max(8, r.left), window.innerWidth - ancho - 8);
      const abajo = window.innerHeight - r.bottom > 380 || r.top < 380;
      setPos({ top: abajo ? r.bottom + 6 : Math.max(8, r.top - 6 - Math.min(380, window.innerHeight - 16)), left });
    } else setPos(null);
    setAbierto(true);
  };
  const cerrarLuego = () => { if (esMovil()) return; timer.current = window.setTimeout(() => setAbierto(false), 180); };
  const cancelarCierre = () => { if (timer.current) { window.clearTimeout(timer.current); timer.current = null; } };

  useEffect(() => {
    if (!abierto) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false); };
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || ancla.current?.contains(t)) return;
      setAbierto(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); document.removeEventListener('touchstart', onDown); };
  }, [abierto]);

  return (
    <div className="relative inline-block max-w-full" data-testid="uso-celda" onMouseEnter={abrir} onMouseLeave={cerrarLuego}>
      <button ref={ancla} type="button" onClick={() => (abierto ? setAbierto(false) : abrir())} aria-expanded={abierto} aria-haspopup="dialog"
        className="text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50" data-testid="uso-abrir" disabled={!uso}>
        <UsoChips uso={uso} cargando={cargando} />
      </button>
      {/* Portal sobre <body>: las tablas van dentro de contenedores con animación por transform, y un
          `position: fixed` ahí dentro se desplaza con el contenedor (QA 24-oct: el panel salía fuera de pantalla). */}
      {abierto && uso && createPortal(
        pos ? (
          <div ref={panel} role="dialog" data-testid="uso-popover" onMouseEnter={cancelarCierre} onMouseLeave={cerrarLuego}
            className="fixed z-50 w-[340px] max-h-[380px] overflow-y-auto bg-surface border border-line rounded-lg shadow-xl p-4 animate-fade-in"
            style={{ top: pos.top, left: pos.left }}>
            <UsoDetalle uso={uso} tenantId={tenantId} conEnlaceFicha />
          </div>
        ) : (
          <div className="fixed inset-0 z-50 flex items-end sm:hidden" data-testid="uso-hoja">
            <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
            <div ref={panel} role="dialog" data-testid="uso-popover" className="relative w-full max-h-[80vh] overflow-y-auto bg-surface rounded-t-2xl shadow-xl p-4 pb-6 animate-fade-in-up">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-content truncate pr-2">{nombre || 'Cómo lo usan'}</h3>
                <button type="button" onClick={() => setAbierto(false)} className="p-1 rounded-full hover:bg-canvas" aria-label="Cerrar" data-testid="uso-cerrar"><X className="h-5 w-5 text-content-3" /></button>
              </div>
              <UsoDetalle uso={uso} tenantId={tenantId} conEnlaceFicha />
            </div>
          </div>
        ),
        document.body,
      )}
    </div>
  );
};
